/**
 * Maps the public property API payload (nested ADL-010) into the flat
 * view-model consumed by PropertyPublicView. Prevents React crashes when
 * nested objects (e.g. description) would otherwise be rendered as children.
 */

import {
  propertyCategory,
  propertyCityState,
  propertyHeadline,
  propertyPricing,
  propertyTotalArea,
  propertyTransactionTypes,
} from './property-display';

type AnyRecord = Record<string, unknown>;

export interface PublicPropertyView {
  property_title: string;
  category?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  description?: string;
  total_area?: number;
  constructed_area?: number;
  bathrooms?: number;
  parking_spots?: number;
  sale?: { price?: number };
  rental?: { price?: number };
  images?: string[];
  housing?: { bedrooms?: number; suites?: number };
  leisure_areas?: string[];
  facilities?: string[];
  security?: string[];
  agency_name?: string;
  condominium?: { leisure_areas?: string[]; security?: string[]; price?: number };
  transaction_type?: string[];
}

/**
 * Reads a nested object section when present.
 *
 * @param raw - Property payload.
 * @param key - Section key.
 * @returns The section object, or an empty object.
 */
function section(raw: AnyRecord, key: string): AnyRecord {
  const value = raw[key];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as AnyRecord)
    : {};
}

/**
 * Returns the first defined candidate.
 *
 * @param values - Candidates in priority order.
 * @returns The first defined value, or undefined.
 */
function firstDefined<T>(...values: Array<T | null | undefined>): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

/**
 * Resolves body description text (full_text or legacy string). Headline alone
 * is used for the title, not duplicated as the description body.
 *
 * @param raw - Property payload.
 * @returns Plain-text description when available.
 */
function descriptionText(raw: AnyRecord): string | undefined {
  const desc = raw.description;
  if (typeof desc === 'string') return desc;
  if (desc && typeof desc === 'object') {
    const nested = desc as AnyRecord;
    return nested.full_text as string | undefined;
  }
  return undefined;
}

/**
 * Reads a string-array field from a section or flat payload.
 *
 * @param primary - Preferred array.
 * @param fallback - Flat fallback array.
 * @returns A string array, or undefined when both empty.
 */
function stringList(primary: unknown, fallback: unknown): string[] | undefined {
  if (Array.isArray(primary)) return primary as string[];
  if (Array.isArray(fallback)) return fallback as string[];
  return undefined;
}

/**
 * Maps nested condominium section fields onto the public view block.
 *
 * @param condo - Nested condominium section.
 * @returns Condominium view block when any field is present.
 */
function mapCondoFromSection(condo: AnyRecord): PublicPropertyView['condominium'] {
  const price = condo.monthly_fee as number | undefined;
  const leisure = stringList(condo.leisure_areas, undefined);
  const security = stringList(condo.security, undefined);
  if (price === undefined && !leisure && !security) return undefined;
  return { price, leisure_areas: leisure, security };
}

/**
 * Maps condominium VO fields onto the public view condominium block.
 *
 * @param raw - Property payload.
 * @returns Condominium view block when any field is present.
 */
function mapCondominium(raw: AnyRecord): PublicPropertyView['condominium'] {
  const flat = raw.condominium;
  if (flat && typeof flat === 'object' && 'price' in (flat as AnyRecord)) {
    return flat as PublicPropertyView['condominium'];
  }
  return mapCondoFromSection(section(raw, 'condominium'));
}

/**
 * Maps nested characteristics onto housing + area fields.
 *
 * @param raw - Property payload.
 * @returns Partial view fields from characteristics.
 */
function mapCharacteristics(raw: AnyRecord): Partial<PublicPropertyView> {
  const chars = section(raw, 'characteristics');
  const bedrooms = firstDefined(
    chars.bedrooms as number | undefined,
    (raw.housing as AnyRecord | undefined)?.bedrooms as number | undefined,
  );
  const suites = firstDefined(
    chars.suites as number | undefined,
    (raw.housing as AnyRecord | undefined)?.suites as number | undefined,
  );
  return {
    total_area: propertyTotalArea(raw),
    constructed_area: firstDefined(
      chars.constructed_area as number | undefined,
      raw.constructed_area as number | undefined,
    ),
    bathrooms: firstDefined(
      chars.bathrooms as number | undefined,
      raw.bathrooms as number | undefined,
    ),
    parking_spots: firstDefined(
      chars.parking_spots as number | undefined,
      raw.parking_spots as number | undefined,
    ),
    housing:
      bedrooms !== undefined || suites !== undefined
        ? { bedrooms, suites }
        : (raw.housing as PublicPropertyView['housing']),
  };
}

/**
 * Maps the public API property payload into the PropertyPublicView model.
 *
 * @param payload - Nested or legacy-flat public property JSON.
 * @returns A flat view model safe to render.
 */
export function toPublicPropertyView(payload: unknown): PublicPropertyView {
  const raw = (payload && typeof payload === 'object' ? payload : {}) as AnyRecord;
  const loc = section(raw, 'location');
  const { city, state } = propertyCityState(raw);
  const pricing = propertyPricing(raw);
  const amenities = section(raw, 'amenities');
  const title = propertyHeadline(raw) || '';

  return {
    property_title: title,
    category: propertyCategory(raw),
    neighborhood: firstDefined(
      loc.neighborhood as string | undefined,
      raw.neighborhood as string | undefined,
    ),
    city,
    state,
    description: descriptionText(raw),
    ...mapCharacteristics(raw),
    sale: pricing.sale,
    rental: pricing.rental,
    images: Array.isArray(raw.images) ? (raw.images as string[]) : undefined,
    leisure_areas: stringList(amenities.internal, raw.leisure_areas),
    facilities: stringList(amenities.external, raw.facilities),
    security: stringList(amenities.security, raw.security),
    agency_name: raw.agency_name as string | undefined,
    condominium: mapCondominium(raw),
    transaction_type: propertyTransactionTypes(raw),
  };
}
